/** Backend refusal for any change to a closed walk-in. */
export const RESERVATION_COMPLETED = 'reservation_completed';

/**
 * The server refused a change because the walk-in is closed: 409 with
 * `code: 'reservation_completed'` in the body (the reservation routes put
 * their error code in `code`).
 */
export function isCompletedRefusal(error: unknown): boolean {
  const err = error as { status?: number; error?: { code?: unknown } } | null;
  return err?.status === 409 && err.error?.code === RESERVATION_COMPLETED;
}

/**
 * True for a closed walk-in: the waiter closed its servis and the server set
 * `status = 'completed'`. Such a reservation is history and is shown
 * read-only (no edit, status change or cancel). The status is read from the
 * row (`status`, `reservation_status`), else from `additional_info`, which
 * may arrive as JSON text.
 */
export function isCompletedReservation(reservation: unknown): boolean {
  if (!reservation || typeof reservation !== 'object') return false;
  const row = reservation as Record<string, unknown>;
  let info = row['additional_info'];
  if (typeof info === 'string') {
    try {
      info = JSON.parse(info);
    } catch {
      info = null;
    }
  }
  const extra = (info && typeof info === 'object' ? info : {}) as Record<string, unknown>;
  const status = [row['status'], row['reservation_status'], extra['status'], extra['reservation_status']]
    .find((value) => value !== null && value !== undefined && String(value).trim() !== '');
  return String(status ?? '').trim().toLowerCase() === 'completed';
}

/**
 * True for a walk-in: the quiet reservation the waiter screen writes when it
 * seats a guest who has no booking, open (`confirmed`) or closed
 * (`completed`). Only the row's `source` marks it. The backend PDF route
 * applies the same rule.
 */
export function isWalkIn(reservation: unknown): boolean {
  if (!reservation || typeof reservation !== 'object') return false;
  const source = (reservation as Record<string, unknown>)['source'];
  return String(source ?? '').trim().toLowerCase() === 'walk-in';
}
